import React, { useState } from 'react';
import { Button, Table } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { COLORS } from '@theme/styles';
import { Tag } from '@components/common';
import CouponsActions, { CouponsSelectors } from '@redux/CouponsRedux';
import { formatDate } from '@utils/date';
import { Link } from 'react-router-dom';
import { TableHeader, TableFooter } from '@components/table';
import { filterItems, sorter, paginateItems } from '@utils/filters';
import { transformStripePrice } from '@services/transform';

import './CouponsTable.less';

const dataColumns = [
  {
    title: 'Code',
    children: [
      {
        title: 'Title & Alias',
        dataIndex: 'title',
        render: (value, row) => (
          <>
            <b>{value} </b>
            <div
              style={{
                maxWidth: 500,
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap'
              }}
            >
              {row.aliases &&
                row.aliases
                  .map(item => (item.status === 'active' ? item.name : null))
                  .filter(item => !!item)
                  .map(name => (
                    <div style={{ padding: '1px 6px' }} key={name}>
                      {name}
                    </div>
                  ))}
            </div>
          </>
        ),
        sorter: (a, b) => sorter(a.title, b.title)
      },
      {
        title: 'Used',
        dataIndex: 'aliases',
        render: value => {
          return (
            <ul>
              <br />
              {value &&
                value.map(({ used }, index) => {
                  return (
                    <>
                      <li key={index}>{used}</li>
                    </>
                  );
                })}
            </ul>
          );
        },
        sorter: (a, b) => sorter(a.aliases, b.aliases)
      }
    ]
  },
  {
    title: 'Discount',
    dataIndex: 'amount',
    render: (val, row) => {
      const { amount, percentage } = row;
      return percentage ? `${percentage}%` : `$${transformStripePrice(amount)}`;
    },
    sorter: (a, b) => sorter(a.amount, b.amount)
  },
  {
    title: 'Date Created',
    dataIndex: 'createdAt',
    render: value => <>{formatDate(value)}</>,
    sorter: (a, b) =>
      sorter(moment(a.createdAt).unix(), moment(b.createdAt).unix())
  },
  {
    title: 'Expiration Date',
    dataIndex: 'expireAt',
    render: value => <>{formatDate(value)}</>,
    sorter: (a, b) => sorter(a.expireAt, b.expireAt)
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: value => (
      <Tag
        color={COLORS[value]}
        content={value === 'active' ? 'Active' : 'InActive'}
      />
    ),
    sorter: (a, b) => sorter(a.status, b.status)
  },
  {
    title: 'Office',
    dataIndex: 'office',
    sorter: (a, b) => sorter(a.office, b.office)
  },
  {
    title: 'Service Type',
    dataIndex: 'serviceType',
    render: value => (value ? value.join(', ') : ''),
    sorter: (a, b) => sorter(a.serviceType, b.serviceType)
  },
  {
    title: 'frequency',
    dataIndex: 'type',
    sorter: (a, b) => sorter(a.type, b.type)
  },
  {
    title: 'No. of usage per client',
    dataIndex: 'usagePerUser',

    sorter: (a, b) => sorter(a.usagePerUser, b.usagePerUser)
  },
  {
    title: 'No. of usage in Total',
    dataIndex: 'usageTotal',
    sorter: (a, b) => sorter(a.usageTotal, b.usageTotal)
  },
  {
    title: 'Days',
    dataIndex: 'days',
    render: value => (value ? value.join(', ') : ''),
    sorter: (a, b) => sorter(a.days, b.days)
  },
  {
    title: () => (
      <Button type="primary">
        <Link to="/coupons/create">Create</Link>
      </Button>
    ),
    key: 'action',
    width: 100,
    render: (value, row) => (
      <div className="flex ai-ct">
        <Button type="primary">
          <Link to={`/coupons/${row.couponID}`}>View details</Link>
        </Button>
      </div>
    )
  }
];

function Coupons(props) {
  const {
    coupons,
    loading,
    listOfTitles,
    listOfUsagePerUser,
    listOfServiceType,
    listOfFrequency,
    listOfOffice,
    reloadData
  } = props;

  const [filters, updateFilters] = useState({
    status: ['active'],
    couponID: [],
    serviceType: [],
    type: [],
    usagePerUser: [],
    discount: [],
    createdAt: [],
    expireAt: []
  });

  const [noOfItemsToDisplay, changeNoOfItemsToDisplay] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);

  const handlePageChange = pageNumber => {
    setCurrentPage(pageNumber);
  };
  const handleChangeNoOfItemsToDisplay = items => {
    changeNoOfItemsToDisplay(items);
    handlePageChange(1);
  };

  const clearFilters = () => {
    updateFilters({
      status: ['active'],
      couponID: [],
      type: [],
      serviceType: [],
      usagePerUser: [],
      discount: [],
      createdAt: [],
      expireAt: []
    });
  };

  const handleFiltersUpdate = (filterType, val) => {
    updateFilters({
      ...filters,
      [filterType]: [...val]
    });
  };

  const header = {
    filters: [
      {
        key: 'couponID',
        type: 'tree',
        label: 'Title',
        data: listOfTitles,
        values: filters.couponID
      },
      {
        key: 'createdAt',
        type: 'date',
        label: 'Create',
        values: filters.createdAt
      },
      {
        key: 'expireAt',
        type: 'date',
        label: 'Expiry',
        values: filters.expireAt
      },

      {
        key: 'status',
        type: 'select',
        data: ['active', 'deactivated'],
        label: 'Status',
        values: filters.status
      },
      {
        key: 'serviceType',
        type: 'select',
        label: 'Service',
        data: listOfServiceType,
        values: filters.serviceType
      },
      {
        key: 'type',
        type: 'select',
        label: 'Frequency',
        data: listOfFrequency,
        values: filters.type
      },
      {
        key: 'office',
        type: 'select',
        label: 'Office',
        data: listOfOffice,
        values: filters.office
      },
      {
        key: 'usagePerUser',
        type: 'select',
        label: 'Usage per User',
        data: listOfUsagePerUser,
        values: filters.usagePerUser
      }
    ],
    updateFilter: handleFiltersUpdate,
    clearFilters,
    reloadData
  };

  const filteredItems = filterItems(coupons, filters, 'coupon');
  const paginatedItems = paginateItems(
    filteredItems,
    currentPage,
    noOfItemsToDisplay
  );

  return (
    <div className="table-view">
      <Table
        columns={dataColumns}
        dataSource={paginatedItems}
        bordered
        rowKey="couponID"
        pagination={false}
        loading={loading}
        title={() => <TableHeader {...header} />}
        footer={() => (
          <TableFooter
            changeNoOfItemsToDisplay={handleChangeNoOfItemsToDisplay}
            noOfItemsToDisplay={noOfItemsToDisplay}
            currentItemsDisplayed={filteredItems.length}
            totalItems={coupons.length}
            handlePageChange={handlePageChange}
            currentPage={currentPage}
            count={filteredItems.length}
          />
        )}
      />
    </div>
  );
}

Coupons.propTypes = {
  coupons: PropTypes.array,
  loading: PropTypes.bool,
  noOfItemsToDisplay: PropTypes.number,
  listOfTitles: PropTypes.array,
  listOfUsagePerUser: PropTypes.array,
  listOfUsageTotal: PropTypes.array,
  listOfPropertyID: PropTypes.array,
  listOfServiceType: PropTypes.array,
  listOfOffice: PropTypes.array,
  listOfFrequency: PropTypes.array,
  reloadData: PropTypes.func
};

const mapStateToProps = state => ({
  coupons: CouponsSelectors.selectCouponsList(state),
  loading: CouponsSelectors.selectLoading(state),
  noOfItemsToDisplay: CouponsSelectors.selectNoOfItemsDiplay(state),
  listOfTitles: CouponsSelectors.selectList(state).title,
  listOfUsagePerUser: CouponsSelectors.selectList(state).usagePerUser,
  listOfServiceType: CouponsSelectors.selectList(state).serviceType,
  listOfFrequency: CouponsSelectors.selectList(state).type,
  listOfOffice: CouponsSelectors.selectList(state).office
});

const mapDispatchToProps = dispatch => ({
  reloadData: () => dispatch(CouponsActions.fetchCoupons())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Coupons);
